import { useCallback, useContext } from 'react';

import getReversedGroupingField from 'hooks/useMetricMatrix/format/getReversedGroupingField';

import { MatrixSortContext } from '../MatrixSortContext';
import { SortToggleContext } from '../../../V5GadgetForm/SortToggleContext';

// A header sort on the matrix: re-sorts the query (MatrixSortContext) and,
// for metric and dimension columns, remembers it as the gadget's default
// sort. Shared by the AG grid's sortChanged listener and DataTable.
const useApplyMatrixSortChange = (chartDefinition: V5ChartDefinition) => {
  const { setDefaultSort } = useContext(SortToggleContext);
  const { setSortField, setSortDirection } = useContext(MatrixSortContext);

  return useCallback(
    ({ colId, sort }: { colId: string; sort: 'asc' | 'desc' }) => {
      setSortField(colId);
      setSortDirection(sort);

      const foundMetric = chartDefinition.series.find(
        (m) => m.metricId === colId,
      );
      // In reversed-axes mode the left grouping column is not
      // dimensionA.field - it holds the dimensionB values (2D) or the
      // literal 'metric' (1D). Recognise either so a dimension sort click
      // persists like a normal matrix.
      const isReversedGroupingColumn =
        !!chartDefinition.reverseAxes &&
        (colId ===
          getReversedGroupingField({
            chartDef: chartDefinition,
            isTwoDimensional: true,
          }) ||
          colId ===
            getReversedGroupingField({
              chartDef: chartDefinition,
              isTwoDimensional: false,
            }));
      if (
        foundMetric ||
        chartDefinition.dimensionA?.field === colId ||
        isReversedGroupingColumn
      ) {
        setDefaultSort({
          field: colId,
          direction: sort,
          isComparison: false,
          isAbsolute: false,
          metricId: foundMetric?.metricId || undefined,
        });
      }
    },
    [chartDefinition, setDefaultSort, setSortDirection, setSortField],
  );
};

export default useApplyMatrixSortChange;
